import {MatDialog} from '@angular/material';
import {Injectable} from '@angular/core';
import {ConfigurationService} from './impl/configuration/configuration.service';
import {NavigateConfig} from '../configuration/navigate-config';
import {StepConfig} from '../configuration/step-config';
import {ActivatedRoute, NavigationStart, Router} from '@angular/router';
import {NavigateApi} from './api/navigate.api';
import {PaymentService} from './impl/payment.service';
import {NotificationService} from './impl/notification.service';
import {StepValidationStorageService} from '../../shared/services/step-validation-storage.service';
import {runtimeConfig} from '../../shared/utils/config-utils';
import * as _ from 'lodash';
import {ScrollHelper} from '../../shared/services/scroll-helper';
import {ApplicationStateService} from '../../shared/services/application-state.service';
import {BusinessParamName} from '../../shared/models/request-offer/request-offer.enum';
import {DialogService} from './dialog.service';
import {SalesChannel} from '../consts/sales-channel.enum';
import {SessionService} from './impl/session/session.service';
import {SessionStorage} from './global/storage/session-storage.service';
import {StepKey} from '../enums/step-key.enum';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {CalculationDataError, CalculationValidationService} from './impl/calculation-validation.service';
import {DialogAction, DialogKey} from '../../shared/components/dialogs/dialogs-configuration';
import {navigateBack, salesChannel} from '../utils/utils';
import {
  AztecScannerDialogComponent
} from '../../shared/components/dialogs/aztec-scanner-dialog/aztec-scanner-dialog.component';
import {EventBusService} from './impl/event-bus.service';
import {DeviceService} from '../../shared/services/device.service';
import {MetricsService} from '../../shared/services/metrics.service';
import {CustomMetricsName} from '../../shared/components/models/metrics/custom-metrics-name.enum';
import {MetricsType} from '../../shared/components/models/metrics/metrics-type.enum';
import {IframeCommunicationService} from '../../shared/services/iframe-communication.service';
import {UrlParamsService} from './impl/url-params.service';
import {AppMode} from '../../shared/models/app-mode.enum';
import {MissingCalculationParam} from '../consts/missing-calculation-param.enum';
import {TranslateService} from '@ngx-translate/core';
import {
  ModifiedOfferPriceChangeDialogComponent
} from '../../shared/components/dialogs/modified-offer-price-change-dialog/modified-offer-price-change-dialog.component';
import {OfferModificationsService} from './impl/offer-modifications/offer-modifications.service';
import {SalesChannelAnalyticsService} from '../../shared/services/sales-channel-analytics.service';
import {CalculationCacheService} from './impl/calculation-cache.service';
import {ApplicationContextService} from './impl/configuration/application-context.service';
import {QueryParamsConfig} from '../configs/query-params-config';
import {QueryParamName} from '../enums/query-param-name.enum';
import {TestingPresetsService} from '../../shared/services/debugging/testing-presets.service';
import {TestingPresetKey} from '../enums/debugging/testing-preset-key.enum';
import {NativeIframeCommunicationService} from '../../shared/services/native-iframe-communication.service';

@Injectable({
  providedIn: 'root'
})
export class NavigateManagerService {

  activatedRoute: ActivatedRoute;
  queryParams: any;

  private readonly _firstStep: StepConfig;
  private _navigationConfiguration: NavigateConfig;
  private activeStepKey: string;
  private _navigate: NavigateApi;
  private _currentStep: StepConfig;
  private _currentStepIndex = 0;
  private readonly notificationsUrlSubstring = 'notifications';
  private initialHistoryLength: number;

  constructor(private configuration: ConfigurationService,
              private paymentService: PaymentService,
              private notificationService: NotificationService,
              private router: Router,
              private contextService: ApplicationContextService,
              private eventBusService: EventBusService,
              private scrollHelper: ScrollHelper,
              private stepValidationStorageService: StepValidationStorageService,
              private calculationValidationService: CalculationValidationService,
              private dialog: MatDialog,
              private ts: TranslateService,
              private urlParamsService: UrlParamsService,
              private dialogService: DialogService,
              private sessionService: SessionService,
              private sessionStorage: SessionStorage,
              private deviceService: DeviceService,
              private iframeService: IframeCommunicationService,
              private metricsService: MetricsService,
              private stateService: ApplicationStateService,
              private calculationCacheService: CalculationCacheService,
              private offerModificationsService: OfferModificationsService,
              private salesChannelAnalyticsService: SalesChannelAnalyticsService,
              private nativeIframeCommunicationService: NativeIframeCommunicationService) {
    this._navigationConfiguration = this.configuration.uiConfig.navigateConfig;
    let firstStep: string = this._navigationConfiguration.firstStep[this.wizardConfig.firstStep];

    this.handleUrl();

    this._firstStep = this._navigationConfiguration.steps.find(step => step.stepKey === firstStep);
    if (this.activeStepKey) {
      this._currentStep = this._navigationConfiguration.steps.find(step => step.stepKey === this.activeStepKey);
    } else {
      this._currentStep = this._navigationConfiguration.steps.find(step => step.stepKey === firstStep);
    }
    this.calculateCurrentStepIndex();

    this.initAppMode();
    this.sessionService.uneditableSessionSubject.subscribe(value => {
      if (value && !this.isNotificationStep) {
        setTimeout(() => this.openUneditableSessionDialog(), 0);
      }
    });

    this.handleBrowserNavigation();
  }

  set navigate(value: NavigateApi) {
    this._navigate = value;
  }

  set currentStepIndex(value: number) {
    this._currentStepIndex = value;
  }

  get currentStepIndex(): number {
    return this._currentStepIndex;
  }

  get prevStep(): StepConfig {
    return this.stepsConfig[this._currentStepIndex - 1];
  }

  get currentStep(): StepConfig {
    return this._currentStep;
  }

  get nextStep(): StepConfig {
    return this.stepsConfig[this._currentStepIndex + 1];
  }

  get stepsConfig(): StepConfig[] {
    return this._navigationConfiguration.steps.filter(step => step.mobile === this.wizardConfig.mobile);
  }

  set currentStep(value: StepConfig) {
    this._currentStep = value;
  }

  get wizardConfig(): any {
    return runtimeConfig().appConfig;
  }

  get isNotificationStep(): boolean {
    return this.router.url.includes(this.notificationsUrlSubstring);
  }

  get isFirstStep() {
    return this.activeStepKey === this._firstStep.stepKey;
  }

  get isSecondStep(): boolean {
    return this.activeStepKey === StepKey.SECOND_STEP;
  }

  get isThirdStep(): boolean {
    return this.activeStepKey === StepKey.THIRD_STEP;
  }

  get isOcDetailsStep(): boolean {
    return this._currentStep  && this._currentStep.isOcDetailsStep;
  }

  get isVehicleModelStep(): boolean {
    return this._currentStep ? this._currentStep.stepKey === StepKey.FIRST_STEP_1 : false;
  }

  get isOffersStep(): boolean {
    return this._currentStep && this._currentStep.isOfferStep;
  }

  get isNextStepOffers(): boolean {
    return this.nextStep ? this.nextStep.isOfferStep : false;
  }

  get isPrevStepOffers(): boolean {
    return this.prevStep ? this.prevStep.isOfferStep : false;
  }

  get isOrderPolicyStep(): boolean {
    return this._currentStep ? this._currentStep.isPrepareOrderPolicyStep : false;
  }

  get offersStepKey(): StepKey {
    return this.stepsConfig.find(stepConfig => stepConfig.isOfferStep).stepKey;
  }

  get afterOffersStepKey(): StepKey {
    const offersStepIndex = this.stepsConfig.findIndex(stepConfig => stepConfig.isOfferStep);
    return this.stepsConfig[offersStepIndex + 1].stepKey;
  }

  get beforeOffersStepKey(): StepKey {
    const offersStepIndex = this.stepsConfig.findIndex(stepConfig => stepConfig.isOfferStep);
    return this.stepsConfig[offersStepIndex - 1].stepKey;
  }

  get isWaitingForOfferMode(): boolean {
    return this.stateService.isWaitingForOfferMode;
  }

  get canCreateNewSession(): boolean {
    return this.stateService.salesChannel !== SalesChannel.YANOSIK;
  }

  /**
   * returns steps configs with unique step keys (no substeps)
   */
  get distinctStepKeysConfigs(): StepConfig[] {
    return _.uniqBy(this._navigationConfiguration.steps, 'stepKey');
  }

  init() {
    this._resolveQueryParams();
  }

  goNext(disconnectWs: boolean = true) {
    let goNext = this._navigate.goNext(disconnectWs);
    if (goNext) {
      this.scrollHelper.scrollToTop();
    }
  }

  goPrev() {
    this._navigate.goPrev();
  }

  getNext(): StepConfig {
    if (this.currentStep.isPrepareOrderPolicyStep && this.currentStep.isSummaryStep) {
      this.iframeService.onStepChange();
      this.prepareWithSubmitOrderPolicy();
    }

    if (this._currentStep.nextStepKey === this._currentStep.stepKey && this._currentStep.hasOwnProperty('substepIndex')) {
      const nextSubstepIndex = this._currentStep.substepIndex + 1;
      this._currentStep = this._navigationConfiguration.steps.find(step => step.stepKey === this._currentStep.stepKey && step.substepIndex === nextSubstepIndex);
      this.updateQueryParams({
        [QueryParamName.STEP_KEY]: this._currentStep.stepKey
      });
      this.calculateCurrentStepIndex();
      return this._currentStep;
    }

    let nextStepKey = this._currentStep.nextStepKey;

    if (nextStepKey != null) {
      this._currentStep = this._navigationConfiguration.steps.find(step => step.stepKey === nextStepKey);
      this.updateQueryParams({
        [QueryParamName.STEP_KEY]: nextStepKey
      });
      this.calculateCurrentStepIndex();
      return this._currentStep;
    } else {
      return null;
    }
  }

  performAdditionalStepValidation(stepKey: string = this.activeStepKey): boolean {
    const errors = this.calculationValidationService.validateStep(stepKey);
    const error = errors && !!errors.length ? errors[0] : null;

    if (isNotNullOrUndefined(error)) {
      switch (error) {
        case CalculationDataError.UNSUPPORTED_DATA:
          this.dialogService.open(DialogKey.UNSUPPORTED_DATA);
          break;
        case CalculationDataError.NOT_UNIQUE_PESEL_NUMBERS:
          this.dialogService.open(DialogKey.NOT_UNIQUE_PESEL_NUMBERS);
          break;
      }
      return false;
    }
    return true;
  }

  getPrev(): StepConfig {
    let prevViewKey = this._currentStep.prevStepKey;
    if (prevViewKey != null) {
      this._currentStep = this._navigationConfiguration.steps.find(step => step.stepKey === prevViewKey);
      this.updateQueryParams({
        [QueryParamName.STEP_KEY]: prevViewKey
      });
      this.calculateCurrentStepIndex();
      return this._currentStep;
    } else {
      return this._firstStep;
    }
  }

  getFirstStep(): StepConfig {
    if (this.activeStepKey) {
      this.currentStep = this.getFirstInvalidStep(this._firstStep.stepKey);
    } else {
      this.currentStep = this._firstStep;
    }

    // consider current substep index from state
    const currentSubstepIndex = this.stateService.state.navigationState.currentSubstepIndex;
    if (isNotNullOrUndefined(currentSubstepIndex)) {
      if (this.currentStep.hasOwnProperty('substepIndex')) {
        this.currentStep = this.getSubstepConfig(this.currentStep.stepKey, currentSubstepIndex);
      }
    }

    this.calculateCurrentStepIndex();
    this.updateQueryParams({
      [QueryParamName.STEP_KEY]: this.currentStep.stepKey
    });
    return this.currentStep;
  }

  getStepConfig(stepKey: StepKey): StepConfig {
    const stepConfig = this._navigationConfiguration.steps.find(step => step.stepKey === stepKey);
    this._currentStep = stepConfig;
    this.updateQueryParams({
      [QueryParamName.STEP_KEY]: stepKey
    });
    this.calculateCurrentStepIndex();

    return stepConfig;
  }

  moveToStep(stepKey: string) {
    this.iframeService.onStepChange();
    this.updateQueryParams({
      [QueryParamName.STEP_KEY]: stepKey
    });
    this._navigate.moveToStep(stepKey);
    if (!this.isWaitingForOfferMode) {
      this.updateAppMode();
    }
  }

  browserNavigate(stepKey: StepKey) {
    this._navigate.browserNavigate(stepKey);
  }

  moveToFirstStep(): void {
    const firstStepKey = this._navigationConfiguration.firstStep[this.wizardConfig.firstStep];
    this.moveToStep(firstStepKey);
    this.scrollHelper.scrollToTop();
  }

  getStepConfigWithValidationFromStart(viewKey: string): StepConfig {
    let stepKeyWithValidationFromStart = this.getStepKeyWithValidationFromStart(viewKey);
    this._currentStep = this.getStep(stepKeyWithValidationFromStart);
    this.calculateCurrentStepIndex();
    return this._currentStep;
  }

  getStepKeyWithValidationFromStart(viewKey: string): StepKey {
    let tempStepKey = this._firstStep.stepKey;
    let substepIndex = this._firstStep.substepIndex;
    while (tempStepKey !== viewKey) {
      if (this.stepValidationStorageService.isValidStep(tempStepKey, substepIndex)) {
        let stepConfig = this.getStep(tempStepKey, substepIndex);
        tempStepKey = stepConfig.nextStepKey;
        if (stepConfig.stepKey === stepConfig.nextStepKey) {
          substepIndex++;
        }
      } else {
        return tempStepKey;
      }
    }
    return tempStepKey;
  }

  getNextStepName() {
    let nextStepKey = this._currentStep.nextStepKey;
    if (nextStepKey != null) {
      let nextStep = this._navigationConfiguration.steps.find(step => step.stepKey === nextStepKey);
      return nextStep.additionalInfo['stepName'];
    } else {
      return null;
    }
  }

  openInitialDialogs(): void {
    if (this.canShowEntryInformationModal()) {
      if (this.isFirstStep) {
        setTimeout(() => this.dialogService.open(DialogKey.ENTRY_INFORMATION), 0);
      }
    } else if (this.canShowAztecScannerModal()) {
      setTimeout(() => this.dialog.open(AztecScannerDialogComponent, {}));
    }
  }

  setAppMode(mode: AppMode): void {
    this.stateService.mode = mode;
  }

  openNoOffersFoundDialog(): void {
    if (!TestingPresetsService.isPresetActive(TestingPresetKey.REJECT_ALL_OFFERS)) {
      this.dialogService.open(DialogKey.NO_OFFERS_FOUND);
      this.dialogService.addAction(DialogKey.NO_OFFERS_FOUND, {
        actionKey: DialogAction.BACK_TO_FIRST_STEP,
        callback: () => {
          if (this.stateService.mode === AppMode.WAITING_FOR_OFFER_LOADING) {
            this.stateService.mode = AppMode.WAITING_FOR_OFFER; // show first step content
          }
          this.moveToFirstStep();
        }
      });
    }
  }

  openMissingPersonalDataDialog(missingPersonalParams: MissingCalculationParam[]): void {
    const missingPersonalParamsString = missingPersonalParams.map(param => {
      switch (param) {
        case MissingCalculationParam.FIRST_NAME:
          return this.ts.instant('MISSING_PERSONAL_DATA_DIALOG_COMPONENT.firstName');
        case MissingCalculationParam.LAST_NAME:
          return this.ts.instant('MISSING_PERSONAL_DATA_DIALOG_COMPONENT.lastName');
        case MissingCalculationParam.PESEL:
          return this.ts.instant('MISSING_PERSONAL_DATA_DIALOG_COMPONENT.pesel');
      }
    }).join(', ');
    const mainText = this.ts.instant('MISSING_PERSONAL_DATA_DIALOG_COMPONENT.mainText', { missingPersonalParams: missingPersonalParamsString });
    this.dialogService.open(DialogKey.MISSING_PERSONAL_DATA, ({mainText}));
    this.dialogService.addAction(DialogKey.MISSING_PERSONAL_DATA, {
      actionKey: DialogAction.BACK_TO_PERSONAL_DATA,
      callback: () => {
        this.dialogService.closeOpenedDialogs();
        this.moveToPersonalDataStep();
      }
    });
  }

  openOfferUpdateDialog(callback): void {
    this.dialogService.open(DialogKey.OFFER_UPDATE);
    this.dialogService.addAction(DialogKey.OFFER_UPDATE, {
      actionKey: DialogAction.SHOW_SECOND_STEP,
      callback: () => callback()
    });
  }

  updateCacheOffer(cacheOffer: boolean): void {
    this.stateService.saveBusinessParameter(BusinessParamName.CACHE_OFFER, cacheOffer);
  }

  markStepAndPreviousStepsAsValid(stepKey: StepKey): void {
    const targetStepIndex = this.getStepIndex(stepKey);
    if (targetStepIndex > -1) {
      for (let stepIndex = 0; stepIndex <= targetStepIndex; stepIndex++) {
        const key = this._navigationConfiguration.steps[stepIndex].stepKey;
        this.markStepAsValid(key);
      }
    }
  }

  markStepAsValid(stepKey: StepKey, substepIndex?: number): void {
    const step = this.getStep(stepKey);
    if (step) {
      if (!isNotNullOrUndefined(substepIndex) && step.hasOwnProperty('substepIndex')) { // mark all substeps as valid if substepIndex is undefined
        this.stepsConfig
            .filter(stepConfig => stepConfig.stepKey === stepKey)
            .map(stepConfig => stepConfig.substepIndex)
            .forEach(index => this.stepValidationStorageService.updateStepValidationStorage(stepKey, true, index));
      } else {
        this.stepValidationStorageService.updateStepValidationStorage(stepKey, true, substepIndex);
      }
    }
  }

  updateAppMode(mode: AppMode = AppMode.RAW): void {
    this.stateService.mode = mode;
  }

  clearCurrentStep(): void {
    this.activeStepKey = null;
    this._currentStepIndex = null;
    this._currentStep = null;
  }

  isTargetStepOffers(stepKey: string): boolean {
    const targetStep: StepConfig = this.stepsConfig.find(stepConfig => stepConfig.stepKey === stepKey);
    return targetStep ? targetStep.isOfferStep : false;
  }

  moveToPersonalDataStep(): void {
    setTimeout(() => {
      this.moveToStep(StepKey.FIRST_STEP_3);
      this.scrollHelper.scrollToTop();
    }, 0);
  }

  private initAppMode(): void {
    if (this.urlParamsService.getQueryParam(QueryParamName.OFFER_ID) && this.isOcDetailsStep) {
      this.stateService.mode = AppMode.WAITING_FOR_OFFER;
    } else {
      this.stateService.mode = this.isNotificationStep ? AppMode.NOTIFICATIONS : AppMode.RAW;
    }
  }

  private handleBrowserNavigation() {
    this.router.events.subscribe(event => {
      if (!(event instanceof NavigationStart)) {
        return;
      }

      if (event.navigationTrigger !== 'popstate') {
        return;
      }

      if (this.dialogService.openedDialogsExist) { // don't navigate - close currently opened dialogs
        this.closeOpenedDialogs();
        return;
      }

      if (this.isFirstStep) {
        const historyShift = this.isUrlInitial(event.url) ? 1 : (window.history.length - this.initialHistoryLength - 1);
        navigateBack(historyShift);
      }

      if (this.isNotificationStep && event.url.includes('stepKey')) { // navigate to first step with new session
        this.browserNavigate(StepKey.FIRST_STEP_0);
        this.eventBusService.nextStepButtonVisibilityEvent.emit(true); // show next step button
        if (this.canCreateNewSession) {
          this.sessionService.resetSession();
          this.stateService.orderPolicySubmitted = false;
        } else {
          setTimeout(() => this.openUneditableSessionDialog(), 0);
        }
        this.paymentService.disconnectWS();
        this.setAppMode(AppMode.RAW);
        this.scrollHelper.scrollToTop();
        return;
      }

      if (this.isOrderPolicyStep) {
        if (this.stateService.orderPolicySubmitted) { // don't allow to leave payment step if order policy was submitted
          setTimeout(() => {
            this.router.navigate([], {
              relativeTo: this.activatedRoute,
              queryParams: { stepKey: this.activeStepKey },
            });
            this.scrollHelper.scrollToTop();
          }, 0);
          return;
        }
      }

      // skip offers step in waiting for offer mode - navigate to first step
      if (this.isWaitingForOfferMode && this.isPrevStepOffers) {
        this.browserNavigate(this.beforeOffersStepKey);
        this.scrollHelper.scrollToTop();
        return;
      }

      // none of above custom navigate situations occured - simply navigate to previous step (according to navigate config)
      if (this.prevStep) {
        this.browserNavigate(this.prevStep.stepKey);
        this.scrollHelper.scrollToTop();
      }
    })
  }

  private isUrlInitial(url: string): boolean {
    return url.includes(QueryParamName.SESSION_ID) || url === '/';
  }

  private closeOpenedDialogs(): void {
    this.dialogService.closeOpenedDialogs();
    setTimeout(() => this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { stepKey: this.activeStepKey }
    }));
  }

  private findStepKeyFromNavigationEvent(event: NavigationStart): StepKey {
    const { url } = event;
    if (url.includes('stepKey')) {
      return <StepKey>url.slice(url.indexOf('=') + 1);
    }
    return null;
  }

  private calculateCurrentStepIndex() {
    this.currentStepIndex = _.findIndex(this.stepsConfig,
      (o) => {
        if (this._currentStep.hasOwnProperty('substepIndex') && o.hasOwnProperty('substepIndex')) {
          return o.stepKey === this._currentStep.stepKey && o.substepIndex === this._currentStep.substepIndex;
        } else {
          return o.stepKey === this._currentStep.stepKey;
        }
      });
  }

  /**
   * ENTRY_INFORMATION dialog is currently disabled;
   * commented line is definition of original requirements for ENTRY_INFORMATION dialog opening
   */
  private canShowEntryInformationModal(): boolean {
    return false;
    // return this.stateService.salesChannel === SalesChannel.YANOSIK && this.isWaitingForOfferMode && this.sessionService.canShowEntryInformationModal && this.stateService.firstEntry;
  }

  get scannerEnabledInSalesChannel(): boolean {
    return salesChannel() !== SalesChannel.YANOSIK && salesChannel() !== SalesChannel.BANKIER;
  }

  /**
   * opening aztec scanner modal on app init is currently disabled;
   * commented line is the definition of original requirements for this action
   */
  private canShowAztecScannerModal(): boolean {
    return false;
    // return this.scannerEnabledInSalesChannel && this.deviceService.isCameraSupported && this.isFirstStep && this.stateService.firstEntry && !this.stateService.sessionDataLoaded;
  }

  private updateQueryParams(queryParams: {[paramName: string]: any }) {
    const paramsConfig = QueryParamsConfig.createParamsConfig(this.contextService, this.deviceService);

    const routerParams = {};
    paramsConfig.forEach(paramConfig => {
      if (paramConfig.removeFromUrl) {
        routerParams[paramConfig.paramName] = null;
      } else {
        if (isNotNullOrUndefined(queryParams[paramConfig.paramName])) {
          routerParams[paramConfig.paramName] = queryParams[paramConfig.paramName];
        }
      }
    });

    this.router.navigate(['/'], {
      queryParams: routerParams,
      queryParamsHandling: 'merge',
      relativeTo: this.activatedRoute
    });
    this.activeStepKey = queryParams[QueryParamName.STEP_KEY];
  }

  private _resolveQueryParams() {
    this.queryParams = this.activatedRoute.snapshot.queryParams;

    if (!this.isNotificationStep) {
      this.updateQueryParams({
        [QueryParamName.STEP_KEY]: this.queryParams[QueryParamName.STEP_KEY]
      });
      this.activeStepKey = this.queryParams[QueryParamName.STEP_KEY];
    }
    this.initialHistoryLength = window.history.length;

    this.salesChannelAnalyticsService.init();
  }

  private getStep(stepKey: string, substepIndex?: number) {
    if (isNotNullOrUndefined(substepIndex)) {
      return this._navigationConfiguration.steps.find(step => step.stepKey === stepKey && step.substepIndex === substepIndex);
    }
    return this._navigationConfiguration.steps.find(step => step.stepKey === stepKey);
  }

  private getFirstInvalidStep(viewKey: string, substepIndex?: number): StepConfig {
    // return this._currentStep = this._navigationConfiguration.steps.find(step => step.stepKey === 'offers')

    let validStep = this.stepValidationStorageService.isValidStep(viewKey, substepIndex);
    if (validStep && viewKey !== this.activeStepKey) {
      let activeStep = this.getStep(viewKey, substepIndex);
      if (activeStep.stepKey === activeStep.nextStepKey) {
        this._currentStep = this.getFirstInvalidStep(activeStep.nextStepKey, activeStep.substepIndex + 1);
      } else {
        this._currentStep = this.getFirstInvalidStep(activeStep.nextStepKey);
      }
    } else {
      this._currentStep = this.getStep(viewKey);
    }
    this.calculateCurrentStepIndex();
    return this._currentStep;
  }

  private prepareWithSubmitOrderPolicy() {
    if (this.offerModificationsService.additionalOptionsModified) {
      this.setAppMode(AppMode.NOTIFICATIONS);
      this.offerModificationsService.postOfferModification(false);
      this.offerModificationsService.modifiedOfferEvent.take(1).subscribe(data => {
        if (data && data.priceChanged) {
          this.setAppMode(AppMode.RAW);
          this.dialog.open(ModifiedOfferPriceChangeDialogComponent, ModifiedOfferPriceChangeDialogComponent.getConfigBuilder().buildConfiguration({}))
              .afterClosed().subscribe(result => {
            if (result.registerPolicy) {
              this.initPayment();
            }
          })
        } else {
          this.initPayment();
        }
      })
    } else {
      this.initPayment();
    }
  }

  private initPayment(): void {
    this.paymentService.initPayment();
    this.calculationCacheService.removeCalculations();
    this.salesChannelAnalyticsService.sendDataAfterPaymentInit();
    this._navigate.clearStep();
    this.setAppMode(AppMode.NOTIFICATIONS);
    this.nativeIframeCommunicationService.onStepCreated(false);
  }

  private handleUrl() {
    this.stateService.entryUrl = window.location.href;
    this.metricsService.sendSimpleMetric(CustomMetricsName.ENTRY_URL, MetricsType.PROCESS, window.location.href);
    if (this.isNotificationStep) {
      this.setAppMode(AppMode.NOTIFICATIONS);
      this.notificationService.initNotification();
      this.paymentService.initPaymentStatus();
    }
  }

  private openUneditableSessionDialog(): void {
    const customConfig = this.buildUneditableSessionDialogConfig();
    this.dialogService.open(DialogKey.UNEDITABLE_SESSION, customConfig);
    if (this.canCreateNewSession) {
      this.dialogService.addAction(DialogKey.UNEDITABLE_SESSION, {
        actionKey: DialogAction.RESET_APPLICATION,
        callback: () => this.sessionService.resetSession()
      });
    }
  }

  private buildUneditableSessionDialogConfig(): any {
    return this.canCreateNewSession ? {
      actionText: 'UNEDITABLE_SESSION_DIALOG_COMPONENT.actionText',
      actionKey: DialogAction.RESET_APPLICATION,
      mainText: 'UNEDITABLE_SESSION_DIALOG_COMPONENT.newSessionAllowedMainText'
    } : {
      mainText: 'UNEDITABLE_SESSION_DIALOG_COMPONENT.newSessionNotAllowedMainText'
    };
  }

  private getSubstepConfig(stepKey: StepKey, substepIndex): StepConfig {
    return this._navigationConfiguration.steps.find(step => step.stepKey === stepKey && step.substepIndex === substepIndex);
  }

  private getStepIndex(stepKey: StepKey): number {
    return this._navigationConfiguration.steps.findIndex(stepConfig => stepConfig.stepKey === stepKey);
  }
}
